/**
 * React integration for @okclawpro/i18n
 *
 * Provides I18nProvider and useTranslation hook.
 * Pattern follows PlatformContext from @okclawpro/ui.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { t as coreT, getLocale, setLocale, onLocaleChange } from './core'
import type { Locale, TranslationKey, TranslationParams } from './types'

interface I18nContextType {
  /** Current locale */
  locale: Locale
  /** Translate a key */
  t: (key: TranslationKey, params?: TranslationParams) => string
  /** Change the locale (async — loads translations) */
  changeLocale: (locale: Locale) => Promise<void>
}

const I18nContext = createContext<I18nContextType>({
  locale: 'en',
  t: coreT,
  changeLocale: setLocale,
})

export interface I18nProviderProps {
  children: ReactNode
  /** Initial locale override (defaults to current core locale) */
  initialLocale?: Locale
}

/**
 * I18nProvider — Wraps the component tree with translation context.
 *
 * Listens to core locale changes and triggers re-renders.
 *
 * Usage:
 * ```tsx
 * <I18nProvider>
 *   <App />
 * </I18nProvider>
 * ```
 */
/**
 * Detect locale from browser environment.
 * Checks navigator.language and maps to supported locales.
 */
function detectBrowserLocale(): Locale {
  if (typeof navigator !== 'undefined') {
    const lang = navigator.language || (navigator as any).userLanguage || ''
    if (lang.startsWith('zh')) return 'zh-CN'
  }
  return 'en'
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  // Auto-detect locale: use initialLocale prop, or current core locale if already set,
  // or detect from browser environment
  const detected = initialLocale ?? (getLocale() !== 'en' ? getLocale() : detectBrowserLocale())
  const [locale, setLocaleState] = useState<Locale>(detected)

  useEffect(() => {
    // Initialize core locale on mount if different from default
    if (detected !== getLocale()) {
      void setLocale(detected)
    }
  }, [detected])

  useEffect(() => {
    // Listen for core locale changes (e.g., from main process IPC)
    const unsubscribe = onLocaleChange((newLocale) => {
      setLocaleState(newLocale)
    })
    return unsubscribe
  }, [])

  const changeLocale = useCallback(async (newLocale: Locale) => {
    await setLocale(newLocale)
    // State update happens via onLocaleChange listener
  }, [])

  const value: I18nContextType = {
    locale,
    t: coreT,
    changeLocale,
  }

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  )
}

/**
 * useTranslation — Access translations in React components.
 *
 * ```tsx
 * const { t, locale } = useTranslation()
 * return <button>{t('ui.copy')}</button>
 * ```
 */
export function useTranslation() {
  return useContext(I18nContext)
}

export default I18nContext
